/* ═══════════════════════ the vocabulary of the work ═══════════════════════

   What kinds of thing studyHub knows about — the kinds an entry on the horizon
   can be, the kinds a week is made of, the kinds a recurring series can take —
   and the small pure functions that read them.

   Two things live here that are not vocabulary but belong beside it: the
   shape of a task's title (`taskShape`, which is how two differently-worded
   problem sets are recognised as the same kind of work), and the two
   statistics the evidence ladder is built from.

   `median` and `p80` are a pair on purpose. The median exists so that one
   catastrophic all-nighter cannot drag the estimate; p80 exists so that it is
   not forgotten either. Nothing here reaches for a mean.

   Pure, and importing nothing at all. */


export const elapsedSec = (t) => (t.accSec || 0) + (t.runSince ? (Date.now() - t.runSince) / 1000 : 0);

export const estBaseline = (t) => (typeof t.baselineEstMin === 'number' ? t.baselineEstMin : t.estMin);
export const wasStarted  = (t) => (t.accSec || 0) > 0 || !!t.runSince;

export const LT_KINDS = [
  { key: 'deadline',   label: 'Deadline',   glyph: '◆',
    explain: 'Something due on a date. Its effort is what makes a week read as heavy.' },
  { key: 'event',      label: 'Event',      glyph: '●',
    explain: 'An exam, a presentation, a conference — it happens at a time and you must be there.' },
  { key: 'trip',       label: 'Trip',       glyph: '✈',
    explain: 'A stretch you are away. Protect it and the planner works around it rather than into it.' },
  { key: 'commitment', label: 'Commitment', glyph: '▮',
    explain: 'An ongoing obligation across a span — a job, a course you are TF-ing, an internship.' },
];

export const LT_HORIZONS = [8, 16, 52];

export const ltKind = (key) => LT_KINDS.find((k) => k.key === key) || LT_KINDS[0];

/* Projected hours landing in one week, and what that reads as. Absolute rather
   than relative to the busiest week: an empty term must not paint itself red. */
export const LT_BANDS = [
  { key: 'quiet',  upTo: 4,        label: 'quiet'  },
  { key: 'steady', upTo: 10,       label: 'steady' },
  { key: 'busy',   upTo: 18,       label: 'busy'   },
  { key: 'crunch', upTo: Infinity, label: 'crunch' },
];

export const ltBand = (hours) => (hours <= 0 ? { key: 'empty', label: 'clear' } : LT_BANDS.find((b) => hours < b.upTo));

export const WK_KINDS = [
  { key: 'lecture', label: 'Lecture',         mode: 'fixed', glyph: '▤',
    explain: 'It happens at a time and you are in the room. Nothing gets planned over it.' },
  { key: 'section', label: 'Section',         mode: 'fixed', glyph: '▤',
    explain: 'Same as a lecture: pinned to its hour, planned around rather than into.' },
  { key: 'hours',   label: 'Office hours',    mode: 'fixed', glyph: '◷',
    explain: 'A window you can use. Pinned, but worth pointing you at when you are stuck.' },
  { key: 'club',    label: 'Club meeting',    mode: 'fixed', glyph: '◇',
    explain: 'A standing commitment that is not coursework. It still takes the evening.' },
  { key: 'shift',   label: 'Shift or job',    mode: 'fixed', glyph: '▮',
    explain: 'Hours you owe someone else. The least movable thing in the week.' },
  { key: 'pset',    label: 'Problem set',     mode: 'work',  glyph: '◆',
    explain: 'Due at a moment, but done whenever you decide. studyHub knows how long yours really take.' },
  { key: 'reading', label: 'Reading',         mode: 'work',  glyph: '▭',
    explain: 'Due before the class it is for. The first thing that gets dropped, so it gets planned first.' },
  { key: 'post',    label: 'Discussion post', mode: 'work',  glyph: '✎',
    explain: 'Small, weekly, and endlessly deferred to the evening it is due.' },
  { key: 'gym',     label: 'Gym',             mode: 'habit', glyph: '⬤',
    explain: 'Wants to happen a few times a week and does not mind when. It fills what the rest leaves.' },
];

export const wkKind = (key) => WK_KINDS.find((k) => k.key === key) || WK_KINDS[5];
export const wkMode = (key) => wkKind(key).mode;

export const WK_SHAPES = [
  { re: /\b(p-?sets?|problem\s+sets?|homeworks?|hw)\b/g,                      as: 'problem set' },
  { re: /\b(discussion\s+posts?|response\s+papers?|weekly\s+posts?|posts?)\b/g, as: 'discussion post' },
  { re: /\b(readings?|chapters?|ch)\b/g,                                      as: 'reading' },
  { re: /\b(essays?|papers?|drafts?)\b/g,                                     as: 'paper' },
  { re: /\b(labs?|lab\s+reports?)\b/g,                                        as: 'lab' },
  { re: /\b(exams?|midterms?|finals?|quiz(?:zes)?|revision)\b/g,              as: 'exam prep' },
  { re: /\b(presentations?|talks?|slides?)\b/g,                               as: 'presentation' },
];

export function taskShape(title) {
  let s = String(title || '').toLowerCase();
  for (const { re, as } of WK_SHAPES) s = s.replace(re, as);
  s = s
    .replace(/\d+(st|nd|rd|th)?/g, ' ')     // "4", "2nd" — the number is never the shape
    .replace(/[^a-z\s]/g, ' ')
    .replace(/\b(the|a|an|for|of|on|in|to|and|part|week|no|my|final)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  /* "Reading — chapter 5" collapses twice, once for each synonym, and would
     key as "reading reading". The shape is a set of words, not a sentence. */
  const seen = [];
  s.split(' ').forEach((w) => { if (w && !seen.includes(w)) seen.push(w); });
  return seen.join(' ') || 'other';
}

/** The middle value, which a single catastrophic all-nighter cannot drag. */
export function median(nums) {
  if (!nums.length) return 0;
  const s = nums.slice().sort((a, b) => a - b);
  const mid = s.length >> 1;
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/* The other end of the same argument. `median` exists so one bad night cannot
   drag the estimate; this exists so it is not forgotten either — the run that
   went long, kept where it can be quoted as "and this much if it runs long"
   without ever being the number anything is scheduled against.

   Nearest-rank, so it is always a value that really happened rather than an
   average of two that did: at two observations it is the worse of them, at
   five it is the fourth. */
export function p80(nums) {
  if (!nums.length) return 0;
  const s = nums.slice().sort((a, b) => a - b);
  return s[Math.ceil(0.8 * s.length) - 1];
}

export const SERIES_KINDS = [
  {
    key: 'reading-post',
    label: 'Reading & discussion post',
    glyph: '✎',
    // what taskShape() should see, so instances bucket with hand-typed siblings
    shape: 'reading discussion post',
    explain: 'The weekly one. How long it takes depends on how many pages and what kind, so it asks before the clock starts.',
    attrs: [
      { key: 'source', label: 'Source', type: 'choice', default: 'secondary',
        options: [
          { value: 'primary',   label: 'Primary',   hint: 'sources, in their own words — slower per page' },
          { value: 'secondary', label: 'Secondary', hint: 'commentary, textbooks, articles about the thing' },
        ] },
      { key: 'pages', label: 'Pages', type: 'quantity', unit: 'page', plural: 'pages', default: 20, min: 1, max: 3000 },
    ],
  },
  {
    key: 'pset',
    label: 'Problem set',
    glyph: '◆',
    shape: 'problem set',
    explain: 'Measured per question, because a six-question set and a twenty-question set are not the same evening.',
    attrs: [
      { key: 'questions', label: 'Questions', type: 'quantity', unit: 'question', plural: 'questions', default: 6, min: 1, max: 200 },
    ],
  },
  {
    key: 'plain',
    label: 'Plain recurring task',
    glyph: '↻',
    shape: '',
    explain: 'Repeats on a schedule and asks nothing before it starts. The estimate is whatever you set.',
    attrs: [],
  },
];

export const seriesKind = (key) => SERIES_KINDS.find((k) => k.key === key) || SERIES_KINDS[SERIES_KINDS.length - 1];

/** The field a kind is measured per, if it has one. */
export const quantityAttr = (kind) => seriesKind(kind).attrs.find((a) => a.type === 'quantity') || null;

/** The fields that split measurements into unlike groups. */
export const choiceAttrs = (kind) => seriesKind(kind).attrs.filter((a) => a.type === 'choice');

/* Three weeks. Far enough that next week's reading is already a task you can
   start early, short enough that the task list stays a list of things you might
   plausibly do rather than a printout of the syllabus. */
